
import { defineComponent, onBeforeMount, reactive, watch } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewTable from '@/ui-kit/NewTable/index.vue'

// @module-components
import RecordContainerRow from '@/modules/NewDispatcher/components/PlanningRecord/RecordContainerRow.vue'
import RecordContainerInvoiceRow from '@/modules/NewDispatcher/components/PlanningRecord/RecordContainerInvoiceRow.vue'
import ModalPanel from '@/modules/NewDispatcher/components/ModalPanel/index.vue'
import ContainersPageInvoicePanel from '@/modules/NewDispatcher/components/ContainersPageInvoicePanel/index.vue'

// @types
import {
    Container,
    ContainersExportExcelParamsT,
    GetContainersParams,
} from '@/new-services/types/containers'
import { UserT } from '@/core/types/users.types'
import { StatusType } from '@/new-services/types/status-type'
import { Courier } from '@/new-services/types/couriers'
import { Sector } from '@/new-services/types/sectors'

// @services
import containersAPI from '@/new-services/containers.service'
import statusTypeAPI from '@/new-services/status-type.service'
import couriersAPI from '@/new-services/couriers.service'
import sectorsAPI from '@/new-services/sectors.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import { useFile } from '@/common/composable/useFile'
import { users } from '@/core/realisations/users.realisation'
import {
    PlanningRecordContainerInvoice,
    PlanningRecordInvoice,
} from '@/new-services/types/planning'
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'

const COLUMNS = [
    {
        name: 'Контейнер',
        key: 'title',
    },
    {
        name: 'Количество накладных',
        key: 'invoiceQuantity',
    },
    {
        name: 'Сектор',
        key: 'sectorName',
    },
    {
        name: 'Физ вес',
        key: 'weight',
    },
    {
        name: 'Объемный вес',
        key: 'volumeWeight',
    },
    {
        name: 'Дата создания',
        key: 'createdAt',
    },
    {
        name: 'Дата доставки',
        key: 'deliveryDate',
    },
    {
        name: 'Статус доставки',
        key: 'deliveryStatus',
    },
    {
        name: 'Курьер',
        key: 'courierName',
    },
    {
        name: 'Диспетчер',
        key: 'userName',
    },
    {
        name: 'Статус контейнера',
        key: 'containerStatus',
    },
]
const STATUSES = [
    { id: 1, name: 'Создан' },
    { id: 2, name: 'Назначен курьер' },
    { id: 3, name: 'Отправлен на сборку' },
    { id: 4, name: 'Собран' },
    { id: 5, name: 'Частично собран' },
    { id: 6, name: 'Создан маршрутный лист' },
    { id: 7, name: 'Выбрана быстрая доставка' },
]

export default defineComponent({
    components: {
        PageTitle,
        NewButton,
        FailedFetch,
        NewTable,
        NewLimit,
        NewPagination,
        NewSelect,
        NewInput,
        Loader,
        //
        RecordContainerRow,
        RecordContainerInvoiceRow,
        ModalPanel,
        ContainersPageInvoicePanel,
    },
    setup() {
        const notifications = useNotifications()
        const { saveFile } = useFile()
        const new_dispatcher_store = useNewDispatcherStore()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_filters_opened: false,
            list: [] as Container[],
            total_pages: 1,
            total_items: 20,
            expanded_containers: [] as number[],
        })

        const filters_state = reactive({
            page: 1,
            limit: 50,
            //
            invoiceNumber: null as string | null,
            dateFrom: null as string | null,
            dateTo: null as string | null,
            deliveryStatusIds: [] as number[],
            userId: null as number | null,
            courierId: null as number | null,
            title: null as string | null,
            sectorId: null as number | null,
            statusIds: [] as number[],
        })

        const excel_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const invoice_state = reactive({
            is_opened: false,
            is_loading: false,
            invoice: null as PlanningRecordInvoice | null,
        })

        //

        const status_types_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as StatusType[],
        })

        const dispatchers_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as UserT[],
        })

        const couriers_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Courier[],
        })

        const sectors_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Sector[],
        })

        //

        const openInvoicePanel = (invoice: PlanningRecordInvoice) => {
            invoice_state.invoice = { ...invoice }
            invoice_state.is_opened = true
        }

        const closeInvoicePanel = () => {
            invoice_state.is_opened = false
            invoice_state.invoice = null
        }

        const loadContainers = async () => {
            try {
                state.is_loading = true

                const params: GetContainersParams = {
                    ...filters_state,
                }

                const response = await containersAPI.getContainers(params)

                state.list = response.data.data
                state.total_pages = response.data.meta.last_page
                state.total_items = response.data.meta.total

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка контейнеров произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadStatusTypes = async () => {
            try {
                status_types_state.is_loading = true

                const response = await statusTypeAPI.getStatusTypes({
                    page: 1,
                    limit: 9999,
                    typeId: 2,
                })
                status_types_state.list = response.data.data

                status_types_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка типов статусов произошла ошибка',
                })
                status_types_state.is_failed = true
            } finally {
                status_types_state.is_loading = false
            }
        }

        const loadDispatchers = async () => {
            try {
                dispatchers_state.is_loading = true

                const params = {
                    page: 1,
                    limit: 9999,
                    roleId: 16,
                }
                const data = await users.getList(params)

                dispatchers_state.list = data.data

                dispatchers_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка диспетчеров произошла ошибка',
                })
                dispatchers_state.is_failed = true
            } finally {
                dispatchers_state.is_loading = false
            }
        }

        const loadCouriers = async () => {
            try {
                couriers_state.is_loading = true

                const response = await couriersAPI.getCouriers({
                    page: 1,
                    limit: 9999,
                })
                couriers_state.list = response.data.data

                couriers_state.is_failed = false
            } catch (error) {
                console.error(error)
                couriers_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка курьеров произошла ошибка',
                })
            } finally {
                couriers_state.is_loading = false
            }
        }

        const loadSectors = async () => {
            try {
                sectors_state.is_loading = true

                let dispatcherSectorIds: number[] | undefined = undefined

                if (new_dispatcher_store.dispatcherSectorIds.length) {
                    dispatcherSectorIds = new_dispatcher_store.dispatcherSectorIds
                }

                const response = await sectorsAPI.getSectors({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds,
                })

                sectors_state.list = response.data.data
                sectors_state.is_failed = false
            } catch (error) {
                sectors_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке секторов произошла ошибка',
                })
            } finally {
                sectors_state.is_loading = false
            }
        }

        const onChangePage = (page: number) => {
            filters_state.page = page
            loadContainers()
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadContainers()
        }

        const resetFilters = () => {
            filters_state.page = 1
            filters_state.limit = 50

            filters_state.invoiceNumber = null
            filters_state.dateFrom = null
            filters_state.dateTo = null
            filters_state.deliveryStatusIds = []
            filters_state.userId = null
            filters_state.courierId = null
            filters_state.title = null
            filters_state.sectorId = null
            filters_state.statusIds = []

            loadContainers()
        }

        const toggleFilters = () => {
            state.is_filters_opened = !state.is_filters_opened
        }

        const getExcelParams = (): ContainersExportExcelParamsT => {
            return {
                invoiceNumber: filters_state.invoiceNumber,
                dateFrom: filters_state.dateFrom,
                dateTo: filters_state.dateTo,
                deliveryStatusIds: filters_state.deliveryStatusIds,
                userId: filters_state.userId,
                courierId: filters_state.courierId,
                title: filters_state.title,
                sectorId: filters_state.sectorId,
                statusIds: filters_state.statusIds,
            }
        }

        const getCurrentDate = () => {
            const currentDateTime = new Date()
            const offset = currentDateTime.getTimezoneOffset()
            currentDateTime.setMinutes(currentDateTime.getMinutes() - offset)
            const formattedDateTime = currentDateTime.toISOString().slice(0, 19).replace('T', ' ')

            return formattedDateTime || '-'
        }

        const exportExcel = async () => {
            try {
                excel_state.is_loading = true

                const params = getExcelParams()
                const response = await containersAPI.exportExcel(params)

                saveFile(response.data, `Контейнеры (${getCurrentDate()})`)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                excel_state.is_loading = false
            }
        }

        //

        const isContainerExpanded = (container_id: number) => {
            return state.expanded_containers.includes(container_id)
        }

        const toggleContainer = (container_id: number) => {
            if (isContainerExpanded(container_id)) {
                const index = state.expanded_containers.findIndex(
                    (cntr_id) => cntr_id === container_id
                )
                if (index >= 0) {
                    state.expanded_containers.splice(index, 1)
                }
            } else {
                state.expanded_containers.push(container_id)
            }
        }

        const onRowClicked = (row: PlanningRecordContainerInvoice) => {
            console.log(row)
        }

        const submitFilters = () => {
            filters_state.page = 1
            loadContainers()
        }

        //

        onBeforeMount(() => {
            loadContainers()
            loadStatusTypes()
            loadDispatchers()
            loadCouriers()
            loadSectors()
        })

        watch(() => new_dispatcher_store.dispatcherSectorIds, loadSectors)

        //

        return {
            state,
            filters_state,
            excel_state,
            status_types_state,
            dispatchers_state,
            couriers_state,
            invoice_state,
            sectors_state,
            //
            loadContainers,
            loadDispatchers,
            loadCouriers,
            loadSectors,
            onChangePage,
            onLimitChange,
            resetFilters,
            toggleFilters,
            loadStatusTypes,
            exportExcel,
            isContainerExpanded,
            toggleContainer,
            openInvoicePanel,
            closeInvoicePanel,
            onRowClicked,
            submitFilters,
            //
            COLUMNS,
            STATUSES,
        }
    },
})
